import React, {useCallback, useEffect, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import {useLoaderData, useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils, {clearRequiredFieldError} from "../../../../utils/project-utils.js";
import ActivityService from "../../../../services/dispatch/activity-service.js";
import UserService from "../../../../services/web/user-service.js";
import ActivityGarrisonModal from "./components/activity-garrison-modal.jsx";
import ActivityGarrisonList from "./components/activity-garrison-list.jsx";
import ActivityBoTable from "./components/activity-bo-table.jsx";
import ActivityEncounterTable from "./components/activity-encounter-table.jsx";
import GarrisonMaterialCautionTable from "../../garrison/form/components/garrison-material-caution-table.jsx";
import {buildActivityPayload} from "./utils/activity-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;
const REQUIRED_BG = "!bg-[#fdfdee]";
const REQUIRED_LABEl_BG = "!bg-[linear-gradient(to_bottom,transparent_50%,#fdfdee_50%)]";

const initialState = {
    onDuty: "",
    inspector: "",
    inspectorNameWar: "",
    activityObservation: "",
    systemUpdate: "",
    inspectorCreatedName: "",
    systemCreationDate: "",
    lastEdit: null,
};

const ActivityForm = () => {
    const navigate = navigateHook();
    const loaderData = useLoaderData();

    const {id} = useParams();
    const [state, setState] = useState(initialState);
    const [garrisons, setGarrisons] = useState([]);
    const [bos, setBos] = useState([]);
    const [encounters, setEncounters] = useState([]);
    const [materialCaution, setMaterialCaution] = useState([]);

    const [isSaving, setIsSaving] = useState(false);
    const [isSavingAndAdding, setIsSavingAndAdding] = useState(false);
    const [isLoadingGarrisons, setIsLoadingGarrisons] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const savedGarrisonRef = useRef([]);
    const savedBosRef = useRef([]);
    const garrisonRequiredRef = useRef(null);

    useEffect(() => {
        if (garrisons.length > 0) clearRequiredFieldError(garrisonRequiredRef.current);
    }, [garrisons.length]);

    const BO_NAME = loaderData?.bo_name;

    const mergeFetched = (items) => {
        const first = items?.[0] ?? {};
        const {listDispatch = {}, garrison = [], bogcm = [], encounters: fetchedEncounters = [], materialCaution: fetchedMaterialCaution = []} = first;

        setGarrisons(prev => {
            const existing = new Set(prev.map(item => item.id));
            const additions = garrison
                .filter(item => !existing.has(item.id))
                .map(item => {
                    const saved = savedGarrisonRef.current.find(value => value.garrisonId === item.id);
                    return {
                        ...item,
                        listDispatch: listDispatch[item.id] ?? [],
                        garrisonObservation: saved?.garrisonObservation ?? "",
                        savedInspectors: saved?.inspectors ?? [],
                        oldId: saved?.id,
                    };
                });
            return [...prev, ...additions];
        });

        setBos(prev => {
            const existing = new Set(prev.map(item => item.bogcmId));
            const additions = bogcm
                .filter(item => !existing.has(item.bogcmId))
                .map(item => {
                    const saved = savedBosRef.current.find(value => value.bogcmId === item.bogcmId);
                    return {...item, bosObservation: saved?.bosObservation ?? "", oldId: saved?.id};
                });
            return [...prev, ...additions];
        });

        setEncounters(prev => {
            const existing = new Set(prev.map(item => item.encounterId));
            return [...prev, ...fetchedEncounters.filter(item => !existing.has(item.encounterId))];
        });

        setMaterialCaution(prev => {
            const existing = new Set(prev.map(item => item.id));
            return [...prev, ...fetchedMaterialCaution.filter(item => !existing.has(item.id))];
        });
    };

    useQuery({
        queryFn: () => UserService.getUserLogged().then((user) => {
            setState(prev => ({
                ...prev,
                inspector: prev.inspector || user?.id || "",
                inspectorNameWar: prev.inspectorNameWar || user?.namewar || user?.nameWar || "",
            }));
            return user;
        }),
        queryKey: ["logged-user"],
        enabled: !id,
    });

    const {isLoading: isQueryLoading, data} = useQuery({
        queryFn: async () => {
            const data = await ActivityService.get(id).catch(() => null);
            if (!data) {
                navigate("/404");
                return;
            }

            const activity = data?.activity ?? {};

            savedGarrisonRef.current = activity.garrison ?? [];
            savedBosRef.current = activity.bos ?? [];

            setState(prev => ({
                ...prev,
                onDuty: activity.onDuty ?? "",
                inspector: activity.inspector ?? "",
                inspectorNameWar: activity.inspectorNameWar ?? "",
                activityObservation: activity.activityObservation ?? "",
                systemUpdate: activity.systemUpdate ?? "",
                inspectorCreatedName: activity.inspectorNameWar ?? "",
                systemCreationDate: activity.systemCreationDate ?? "",
                lastEdit: data?.lastEdit ?? null,
            }));

            const ids = (activity.garrison ?? []).map(item => item.garrisonId).join(",");
            if (ids) {
                const items = await ActivityService.activityItems(ids);
                mergeFetched(items);
            }

            return data;
        },
        queryKey: ["dispatch-activity", id],
        enabled: !!id,
    });

    const addGarrisons = async (ids) => {
        if (!ids.length) return;
        setIsLoadingGarrisons(true);
        try {
            const items = await ActivityService.activityItems(ids.join(","));
            mergeFetched(items);
        } catch (error) {
            console.error(error);
            projectUtils.swalErrorMessage("", "Não foi possível carregar as guarnições.", "Ok");
        } finally {
            setIsLoadingGarrisons(false);
        }
    };

    const removeGarrison = (garrisonId) => {
        setGarrisons(prev => prev.filter(item => item.id !== garrisonId));
        setBos(prev => prev.filter(item => item.garrisonId !== garrisonId));
        setEncounters(prev => prev.filter(item => item.garrisonId !== garrisonId));
        setMaterialCaution(prev => prev.filter(item => item.garrisonId !== garrisonId));
    };

    const changeGarrisonObservation = useCallback((garrisonId, value) =>
        setGarrisons(prev => prev.map(item => item.id === garrisonId ? {...item, garrisonObservation: value} : item)), []);

    const changeBoObservation = useCallback((bogcmId, value) =>
        setBos(prev => prev.map(item => item.bogcmId === bogcmId ? {...item, bosObservation: value} : item)), []);

    const openModal = () => {
        if (!state.onDuty) {
            projectUtils.swalErrorMessage("", "Informe a data do plantão antes de adicionar guarnições.", "Ok");
            return;
        }
        setShowModal(true);
    };

    const resetForm = () => {
        setState(prev => ({...prev, onDuty: "", activityObservation: "", systemUpdate: ""}));
        setGarrisons([]);
        setBos([]);
        setEncounters([]);
        setMaterialCaution([]);
    };

    const persist = (addAfter = false) => {
        if (!state.onDuty) {
            projectUtils.swalErrorMessage("", "O campo \"Plantão\" é obrigatório.", "Ok");
            return;
        }

        if (!garrisons.length) {
            projectUtils.swalErrorMessage("", "O campo \"Guarnições\" é obrigatório.", "Ok");
            return;
        }

        const payload = buildActivityPayload({...state, id}, garrisons, bos);

        const setSaving = addAfter ? setIsSavingAndAdding : setIsSaving;
        setSaving(true);
        const response = id ? ActivityService.update(id, payload) : ActivityService.save(payload);
        response
            .then((data) => {
                if (addAfter) {
                    if (id) {
                        projectUtils.success(true);
                        navigate(API_DESPACHO_WEB + "/activity/form", false);
                    } else {
                        projectUtils.success(false);
                        resetForm();
                    }
                    return;
                }

                projectUtils.success(true);

                if (id) {
                    navigate(API_DESPACHO_WEB + "/activity", false);
                    return;
                }

                navigate(`${API_DESPACHO_WEB}/activity/form/${data.id}`, false);
            })
            .catch((error) => {
                console.error(error);
                const message = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", message, "Ok");
            })
            .finally(() => setSaving(false));
    };

    const destroy = () => {
        projectUtils.swalMessage("warning", "Excluir", `Excluir o diário de plantão "${state.onDuty}"?`, "Sim", true, "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                ActivityService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(API_DESPACHO_WEB + "/activity", false);
                    })
                    .catch((error) => {
                        console.error(error);
                        const message = error.response?.data?.errorMessages ?? "Não foi possível excluir.";
                        projectUtils.swalErrorMessage("", message, "Ok");
                    });
            });
    };

    const newActivity = () => navigate(API_DESPACHO_WEB + "/activity/form", false);

    const garrisonIds = garrisons.map(item => item.id);
    const showMaterialCaution = materialCaution.length > 0;

    return (
        <Template isLoading={isQueryLoading || isSaving || isSavingAndAdding || isLoadingGarrisons}>
            <Template.Header>
                <Template.Title browserTitle={'Diário de plantão'}>Diário de plantão {id ? <span className="text-[12px]">(ID: {id})</span> : ""}</Template.Title>
                {id &&
                    <Button target="primary-outline" onClick={newActivity}><FaPlus/>Adicionar diário de plantão</Button>
                }
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Header>
                        <Card.Title>Dados</Card.Title>
                    </Card.Header>
                    <Card.Body className="flex flex-col gap-3">
                        <div className="grid grid-cols-12 gap-4 items-center">
                            <InputDatetime
                                classNameWrapper="col-span-3"
                                inputClass={REQUIRED_BG}
                                labelClass={REQUIRED_LABEl_BG}
                                id="onDuty"
                                label="Plantão *"
                                dateFormat="L"
                                returnDateObj={false}
                                selected={state.onDuty}
                                onChange={(value) => setState(prev => ({...prev, onDuty: value ?? ""}))}
                            />
                            {id && data &&
                                <div className="col-span-9 text-sm text-gray-600">
                                    Cadastrado por: <strong>{data?.inspectorNameWar}</strong>
                                    {data?.activity.systemCreation ? <> em <strong>{projectUtils.formatDate(data?.activity.systemCreation)}</strong></> : null}
                                    {state.lastEdit &&
                                        <> Editado por: <strong>{state.lastEdit.userName}</strong> em <strong>{state.lastEdit.date}</strong></>}
                                </div>
                            }
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header className="flex items-center gap-2">
                        <Card.Title>Guarnições</Card.Title>
                        {
                            state?.onDuty &&
                            <div
                                ref={garrisonRequiredRef}
                                className="relative w-fit border border-transparent rounded-lg transition-[border-color,border-width] duration-150 ease-out"
                                data-required-field="true"
                                data-required-has-value={garrisons.length > 0 ? "true" : "false"}
                                data-required-focus="true"
                                tabIndex={-1}
                                aria-label="Guarnições"
                            >
                                <Button className={"py-0! px-3! text-xs h-5.25!"} onClick={openModal}>
                                    <FaPlus/>
                                </Button>
                            </div>
                        }
                    </Card.Header>
                    <Card.Body>
                        <ActivityGarrisonList
                            garrisons={garrisons}
                            boName={BO_NAME}
                            onChangeObservation={changeGarrisonObservation}
                            onRemove={removeGarrison}
                        />
                    </Card.Body>
                </Card>

                {showMaterialCaution &&
                    <Card>
                        <Card.Header className="flex items-center gap-2">
                            <Card.Title>Materiais em cautela</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <GarrisonMaterialCautionTable rows={materialCaution}/>
                        </Card.Body>
                    </Card>
                }

                <Card>
                    <Card.Header>
                        <Card.Title>{BO_NAME}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <ActivityBoTable bos={bos} boName={BO_NAME} onChangeObservation={changeBoObservation}/>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Abordagens</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <ActivityEncounterTable encounters={encounters} boName={BO_NAME}/>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Observação</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <InputTextarea
                            id="activityObservation"
                            label="Observação"
                            textAreaClass="h-25!"
                            value={state.activityObservation}
                            onChange={({target: {value}}) => setState(prev => ({...prev, activityObservation: value}))}
                        />
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {id && <Button target="danger" onClick={destroy} actionFeedbackType='delete'>Excluir</Button>}
                        <Button target="primary-outline" onClick={() => persist(true)} loading={isSavingAndAdding} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => persist(false)} loading={isSaving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>

            <ActivityGarrisonModal
                open={showModal}
                setOpen={setShowModal}
                dateOnDuty={state.onDuty}
                selectedIds={garrisonIds}
                inspectorNameWar={state.inspectorNameWar}
                onConfirm={addGarrisons}
            />
        </Template>
    );
};

export default ActivityForm;